import React from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  Divider,
  Breadcrumbs,
  Link,
  Grid,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import { 
  Lightbulb as LightbulbIcon,
  Psychology as PsychologyIcon,
  Security as SecurityIcon,
  EmojiObjects as EmojiObjectsIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  Telegram as TelegramIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import unwiringTechLogo from '../assets/unwiringtech_logo.png';

const AboutPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link component={RouterLink} to="/" color="inherit">
          Home
        </Link>
        <Typography color="text.primary">About Us</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          About Unwiring Tech®
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
          Pioneering AI-Powered Solutions for the Digital Age
        </Typography>
      </Box>
      
      {/* Main Content */}
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          mb: 5
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" component="h2" gutterBottom>
              🧠 Our Story: From Vision to Innovation
            </Typography>
            <Typography variant="body1" paragraph>
              Unwiring Tech® is not just a company—it's the realization of a lifelong dream to make technology more accessible, impactful, and transformative. Founded by Tanmay Agarwal, Unwiring Tech® was born from a deep-seated passion for technology and a mission to simplify complex digital experiences for everyone.
            </Typography>
            <Typography variant="body1" paragraph>
              Our journey began with a curious kid exploring the inner workings of toys and evolved into a tech-driven venture aimed at "unwiring" technology to benefit individuals, businesses, and society at large.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Box 
                component="img"
                src={unwiringTechLogo}
                alt="Unwiring Tech Logo"
                sx={{ 
                  width: '100%', 
                  maxWidth: 280, 
                  height: 'auto',
                  display: 'block',
                  mb: 3
                }}
              />
              <Box sx={{ 
                p: 3, 
                width: '100%',
                bgcolor: 'rgba(0,26,20,0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(0,26,20,0.1)'
              }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Company Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="body2">
                    <strong>Legal Name:</strong> Unwiring Tech
                  </Typography>
                  <Typography variant="body2">
                    <strong>Founder:</strong> Tanmay Agarwal
                  </Typography>
                  <Typography variant="body2">
                    <strong>Headquarters:</strong> Agra, Uttar Pradesh, India
                  </Typography>
                  <Typography variant="body2">
                    <strong>GSTIN:</strong> 09CYNPA1063Q1ZZ
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> support@unwiringtech.com
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 5 }} />
        
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            🚀 Our Mission
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            Our mission is to create innovative solutions that empower people, solve real-world problems, and enhance digital experiences. We believe in building tools that turn complexity into simplicity, helping users save time, learn faster, and connect more deeply with the world around them.
          </Typography>
        </Box>
        
        <Typography variant="h4" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
          🛠️ Our Ventures
        </Typography>
        
        <Card 
          elevation={0} 
          sx={{ 
            borderRadius: 4,
            bgcolor: 'rgba(0,26,20,0.02)',
            border: '1px solid rgba(0,26,20,0.08)',
            mb: 3
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              1. SharkPanzer
            </Typography>
            <Typography variant="subtitle1" color="primary.main" gutterBottom>
              Experience the Full Story in a Capsule
            </Typography>
            <Typography variant="body1" paragraph>
              SharkPanzer is an AI-powered platform that transforms videos from various sources (YouTube, Instagram, offline videos, or any social media platform) into comprehensive Knowledge Capsules. Our unique approach allows users to consume valuable insights in a fraction of the time, offering formats like Full Capsules, Key Insights, Mind Maps, and more.
            </Typography>
            <Typography variant="body1">
              SharkPanzer aims to help users learn efficiently, ensuring complete information delivery with clarity and precision.
            </Typography>
          </CardContent>
        </Card>
        
        <Card 
          elevation={0} 
          sx={{ 
            borderRadius: 4,
            bgcolor: 'rgba(0,26,20,0.02)',
            border: '1px solid rgba(0,26,20,0.08)',
            mb: 3
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              2. Unwiring Studios
            </Typography>
            <Typography variant="subtitle1" color="primary.main" gutterBottom>
              Where Creativity Meets Innovation
            </Typography>
            <Typography variant="body1">
              Unwiring Studios is the creative division of Unwiring Tech®, specializing in impactful videos, AI-driven visuals, and innovative storytelling. We combine technology and creativity to produce content that not only engages but also inspires. From business storytelling through rap videos to AI reimagined visuals, Unwiring Studios sets a new benchmark in digital content creation.
            </Typography>
          </CardContent>
        </Card>
        
        <Card 
          elevation={0} 
          sx={{ 
            borderRadius: 4,
            bgcolor: 'rgba(0,26,20,0.02)',
            border: '1px solid rgba(0,26,20,0.08)',
            mb: 3
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              3. Giaan3D.ai
            </Typography>
            <Typography variant="subtitle1" color="primary.main" gutterBottom>
              3D Animation Made Simple
            </Typography>
            <Typography variant="body1" paragraph>
              Giaan3D.ai, powered by Unwiring Tech®, is an advanced platform that transforms 2D images into dynamic 3D animations effortlessly. Whether you're a creator, small business, or innovator, Giaan3D.ai offers high-quality, affordable 3D conversions that make your visuals stand out.
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'rgba(0,26,20,0.05)', 
              borderRadius: 2,
              border: '1px solid rgba(0,26,20,0.1)',
              mb: 2
            }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                🏆 Google Startups Cloud Program Approved
              </Typography>
              <Typography variant="body2">
                Giaan3D.ai's innovative approach earned recognition from the prestigious Google Startups Cloud Program, highlighting our commitment to revolutionizing 3D content creation. While we are currently refining the AI model and resolving key challenges, the platform remains accessible for testing. We invite users to explore its capabilities and share feedback as we prepare for a full-scale relaunch.
              </Typography>
            </Box>
            <Typography variant="body2">
              🔗 Explore Giaan3D.ai: <Link href="https://www.giaan3d.ai" target="_blank" rel="noopener noreferrer">www.giaan3d.ai</Link>
            </Typography>
          </CardContent>
        </Card>
        
        <Card 
          elevation={0} 
          sx={{ 
            borderRadius: 4,
            bgcolor: 'rgba(0,26,20,0.02)',
            border: '1px solid rgba(0,26,20,0.08)',
            mb: 3
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              4. UnspokenLove.in
            </Typography>
            <Typography variant="subtitle1" color="primary.main" gutterBottom>
              Express Your Feelings Anonymously
            </Typography>
            <Typography variant="body1">
              UnspokenLove.in is a unique platform that enables users to express their feelings to loved ones anonymously. With automated digital confessions and secure messaging, UnspokenLove.in provides a safe space to share emotions without the fear of direct rejection.
            </Typography>
          </CardContent>
        </Card>
        
        <Card 
          elevation={0} 
          sx={{ 
            borderRadius: 4,
            bgcolor: 'rgba(0,26,20,0.02)',
            border: '1px solid rgba(0,26,20,0.08)',
            mb: 5
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              5. FDroid Console (Private App Hosting)
            </Typography>
            <Typography variant="subtitle1" color="primary.main" gutterBottom>
              Secure Android App Distribution
            </Typography>
            <Typography variant="body1" paragraph>
              Our FDroid Console offers a private server for securely hosting Android applications. This platform is ideal for businesses and developers who want a controlled, private environment to distribute their apps without relying on public app stores.
            </Typography>
            <Typography variant="body2">
              🔗 Visit FDroid Console: <Link href="https://fdroid.unwiringapps.com" target="_blank" rel="noopener noreferrer">fdroid.unwiringapps.com</Link>
            </Typography>
          </CardContent>
        </Card>
        
        <Typography variant="h4" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
          💡 Our Values
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} md={3}>
            <Card 
              elevation={0} 
              sx={{ 
                height: '100%',
                borderRadius: 4,
                bgcolor: 'rgba(0,26,20,0.02)',
                border: '1px solid rgba(0,26,20,0.08)'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <LightbulbIcon />
                  </Avatar>
                  <Typography variant="h6">
                    Innovation
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Continuously pushing the boundaries of what's possible.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card 
              elevation={0} 
              sx={{ 
                height: '100%',
                borderRadius: 4,
                bgcolor: 'rgba(0,26,20,0.02)',
                border: '1px solid rgba(0,26,20,0.08)'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <PsychologyIcon />
                  </Avatar>
                  <Typography variant="h6">
                    Simplicity
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Turning complex technology into easy-to-use solutions.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card 
              elevation={0} 
              sx={{ 
                height: '100%',
                borderRadius: 4,
                bgcolor: 'rgba(0,26,20,0.02)',
                border: '1px solid rgba(0,26,20,0.08)'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <SecurityIcon />
                  </Avatar>
                  <Typography variant="h6">
                    Integrity
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Building trust through transparency and strong ethics.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card 
              elevation={0} 
              sx={{ 
                height: '100%',
                borderRadius: 4,
                bgcolor: 'rgba(0,26,20,0.02)',
                border: '1px solid rgba(0,26,20,0.08)'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <EmojiObjectsIcon />
                  </Avatar>
                  <Typography variant="h6">
                    Impact
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Focusing on real-world solutions that make a difference.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
            🌍 Our Commitment
          </Typography>
          <Typography variant="body1" paragraph>
            We are dedicated to ethical practices, customer satisfaction, and continuous innovation. Every product and service under Unwiring Tech® is developed with a user-first approach, ensuring high-quality standards and meaningful impact.
          </Typography>
        </Box>
      </Paper>
      
      {/* Contact Section */}
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          mb: 4
        }}
      >
        <Typography variant="h4" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          🤝 Connect with Us
        </Typography>
        <Typography variant="body1" paragraph sx={{ textAlign: 'center' }}>
          Whether you're interested in our products, exploring new collaborations, or simply want to learn more about our vision, we'd love to connect!
        </Typography>
        
        <Grid container spacing={2} justifyContent="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ 
              p: 2, 
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <EmailIcon color="primary" />
              <Typography variant="body1">
                <strong>Email:</strong> support@unwiringtech.com
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box sx={{ 
              p: 2, 
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <LanguageIcon color="primary" />
              <Typography variant="body1">
                <strong>Website:</strong> <Link href="https://www.unwiringtech.com" target="_blank" rel="noopener noreferrer">www.unwiringtech.com</Link>
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box sx={{ 
              p: 2, 
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <TelegramIcon color="primary" />
              <Typography variant="body1">
                <strong>Telegram:</strong> @unwiringlife
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        <Typography variant="h6" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
          Let's "unwire" the future together—one innovation at a time!
        </Typography>
      </Paper>
    </Container>
  );
};

export default AboutPage;
